import { browserPerformanceTimeOrigin, getActiveSpan, spanToJSON, setMeasurement, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, parseUrl, htmlTreeAsString, getComponentName } from '@sentry/core';
import { WINDOW } from '../types.js';
import { trackClsAsStandaloneSpan } from './cls.js';
import { addPerformanceInstrumentationHandler, addClsInstrumentationHandler, addLcpInstrumentationHandler, addFidInstrumentationHandler, addTtfbInstrumentationHandler } from './instrument.js';
import { getBrowserPerformanceAPI, msToSec, startAndEndSpan, extractNetworkProtocol, isMeasurementValue } from './utils.js';
import { getActivationStart } from './web-vitals/lib/getActivationStart.js';
import { getNavigationEntry } from './web-vitals/lib/getNavigationEntry.js';
import { getVisibilityWatcher } from './web-vitals/lib/getVisibilityWatcher.js';
var MAX_INT_AS_BYTES = 2147483647;
var _performanceCursor = 0;
var _measurements = {};

var _lcpEntry;

var _clsEntry;
/**
 * Start tracking web vitals.
 * The callback returned by this function can be used to stop tracking & ensure all measurements are final & captured.
 *
 * @returns A function that forces web vitals collection
 */


function startTrackingWebVitals(_ref) {
  var {
    recordClsStandaloneSpans
  } = _ref;
  var performance = getBrowserPerformanceAPI();

  if (performance && browserPerformanceTimeOrigin()) {
    // @ts-expect-error we want to make sure all of these are available, even if TS is sure they are
    if (performance.mark) {
      WINDOW.performance.mark('sentry-tracing-init');
    }

    var fidCleanupCallback = _trackFID();

    var lcpCleanupCallback = _trackLCP();

    var ttfbCleanupCallback = _trackTtfb();

    var clsCleanupCallback = recordClsStandaloneSpans ? trackClsAsStandaloneSpan() : _trackCLS();
    return () => {
      fidCleanupCallback();
      lcpCleanupCallback();
      ttfbCleanupCallback();
      clsCleanupCallback === null || clsCleanupCallback === void 0 ? void 0 : clsCleanupCallback();
    };
  }

  return () => undefined;
}
/**
 * Start tracking long tasks.
 */


function startTrackingLongTasks() {
  addPerformanceInstrumentationHandler('longtask', _ref2 => {
    var {
      entries
    } = _ref2;
    var parent = getActiveSpan();

    if (!parent) {
      return;
    }

    var {
      op: parentOp,
      start_timestamp: parentStartTimestamp
    } = spanToJSON(parent);

    for (var entry of entries) {
      var startTime = msToSec(browserPerformanceTimeOrigin() + entry.startTime);
      var duration = msToSec(entry.duration);

      if (parentOp === 'navigation' && parentStartTimestamp && startTime < parentStartTimestamp) {
        // Skip adding a span if the long task started before the navigation started.
        // `startAndEndSpan` will otherwise adjust the parent's start time to the span's start
        // time, potentially skewing the duration of the actual navigation as reported via our
        // routing instrumentations
        continue;
      }

      startAndEndSpan(parent, startTime, startTime + duration, {
        name: 'Main UI thread blocked',
        op: 'ui.long-task',
        attributes: {
          [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.ui.browser.metrics'
        }
      });
    }
  });
}
/**
 * Start tracking long animation frames.
 */


function startTrackingLongAnimationFrames() {
  // NOTE: the current web-vitals version (3.5.2) does not support long-animation-frame, so
  // we directly observe `long-animation-frame` events instead of through the web-vitals
  // `observe` helper function.
  var observer = new PerformanceObserver(list => {
    var parent = getActiveSpan();

    if (!parent) {
      return;
    }

    for (var entry of list.getEntries()) {
      if (!entry.scripts[0]) {
        continue;
      }

      var startTime = msToSec(browserPerformanceTimeOrigin() + entry.startTime);
      var {
        start_timestamp: parentStartTimestamp,
        op: parentOp
      } = spanToJSON(parent);

      if (parentOp === 'navigation' && parentStartTimestamp && startTime < parentStartTimestamp) {
        // Skip adding the span if the long animation frame started before the navigation started.
        // `startAndEndSpan` will otherwise adjust the parent's start time to the span's start
        // time, potentially skewing the duration of the actual navigation as reported via our
        // routing instrumentations
        continue;
      }

      var duration = msToSec(entry.duration);
      var attributes = {
        [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.ui.browser.metrics'
      };
      var initialScript = entry.scripts[0];
      var {
        invoker,
        invokerType,
        sourceURL,
        sourceFunctionName,
        sourceCharPosition
      } = initialScript;
      attributes['browser.script.invoker'] = invoker;
      attributes['browser.script.invoker_type'] = invokerType;

      if (sourceURL) {
        attributes['code.filepath'] = sourceURL;
      }

      if (sourceFunctionName) {
        attributes['code.function'] = sourceFunctionName;
      }

      if (sourceCharPosition !== -1) {
        attributes['browser.script.source_char_position'] = sourceCharPosition;
      }

      startAndEndSpan(parent, startTime, startTime + duration, {
        name: 'Main UI thread blocked',
        op: 'ui.long-animation-frame',
        attributes
      });
    }
  });
  observer.observe({
    type: 'long-animation-frame',
    buffered: true
  });
}
/**
 * Start tracking interaction events.
 */


function startTrackingInteractions() {
  addPerformanceInstrumentationHandler('event', _ref3 => {
    var {
      entries
    } = _ref3;
    var parent = getActiveSpan();

    if (!parent) {
      return;
    }

    for (var entry of entries) {
      if (entry.name === 'click') {
        var startTime = msToSec(browserPerformanceTimeOrigin() + entry.startTime);
        var duration = msToSec(entry.duration);
        var spanOptions = {
          name: htmlTreeAsString(entry.target),
          op: "ui.interaction.".concat(entry.name),
          startTime: startTime,
          attributes: {
            [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.ui.browser.metrics'
          }
        };
        var componentName = getComponentName(entry.target);

        if (componentName) {
          spanOptions.attributes['ui.component_name'] = componentName;
        }

        startAndEndSpan(parent, startTime, startTime + duration, spanOptions);
      }
    }
  });
}
/**
 * Starts tracking the Cumulative Layout Shift on the current page and collects the value and last entry
 * to the `_measurements` object which ultimately is applied to the pageload span's measurements.
 */


function _trackCLS() {
  return addClsInstrumentationHandler(_ref4 => {
    var {
      metric
    } = _ref4;
    var entry = metric.entries[metric.entries.length - 1];

    if (!entry) {
      return;
    }

    _measurements['cls'] = {
      value: metric.value,
      unit: ''
    };
    _clsEntry = entry;
  }, true);
}
/** Starts tracking the Largest Contentful Paint on the current page. */


function _trackLCP() {
  return addLcpInstrumentationHandler(_ref5 => {
    var {
      metric
    } = _ref5;
    var entry = metric.entries[metric.entries.length - 1];

    if (!entry) {
      return;
    }

    _measurements['lcp'] = {
      value: metric.value,
      unit: 'millisecond'
    };
    _lcpEntry = entry;
  }, true);
}
/** Starts tracking the First Input Delay on the current page. */


function _trackFID() {
  return addFidInstrumentationHandler(_ref6 => {
    var {
      metric
    } = _ref6;
    var entry = metric.entries[metric.entries.length - 1];

    if (!entry) {
      return;
    }

    var timeOrigin = msToSec(browserPerformanceTimeOrigin());
    var startTime = msToSec(entry.startTime);
    _measurements['fid'] = {
      value: metric.value,
      unit: 'millisecond'
    };
    _measurements['mark.fid'] = {
      value: timeOrigin + startTime,
      unit: 'second'
    };
  });
}

function _trackTtfb() {
  return addTtfbInstrumentationHandler(_ref7 => {
    var {
      metric
    } = _ref7;
    var entry = metric.entries[metric.entries.length - 1];

    if (!entry) {
      return;
    }

    _measurements['ttfb'] = {
      value: metric.value,
      unit: 'millisecond'
    };
  });
}
/** Add performance related spans to a transaction */


function addPerformanceEntries(span, options) {
  var performance = getBrowserPerformanceAPI();
  var origin = browserPerformanceTimeOrigin();

  if (!(performance !== null && performance !== void 0 && performance.getEntries) || !origin) {
    // Gatekeeper if performance API not available
    return;
  }

  var timeOrigin = msToSec(origin);
  var performanceEntries = performance.getEntries();
  var {
    op,
    start_timestamp: transactionStartTime
  } = spanToJSON(span);
  performanceEntries.slice(_performanceCursor).forEach(entry => {
    var startTime = msToSec(entry.startTime);
    var duration = msToSec( // Inexplicably, Chrome sometimes emits a negative duration. We need to work around this.
    // There is a SO post attempting to explain this, but it leaves one with open questions: https://stackoverflow.com/questions/23191918/peformance-getentries-and-negative-duration-display
    // The way we clamp the value is probably not accurate, since we have observed this happen for things that may take a while to load, like for example the replay worker.
    // TODO: Investigate why this happens and how to properly mitigate. For now, this is a workaround to prevent transactions being dropped due to negative duration spans.
    Math.max(0, entry.duration));

    if (op === 'navigation' && transactionStartTime && timeOrigin + startTime < transactionStartTime) {
      return;
    }

    switch (entry.entryType) {
      case 'navigation':
        {
          _addNavigationSpans(span, entry, timeOrigin);

          break;
        }

      case 'mark':
      case 'paint':
      case 'measure':
        {
          _addMeasureSpans(span, entry, startTime, duration, timeOrigin); // capture web vitals


          var firstHidden = getVisibilityWatcher(); // Only report if the page wasn't hidden prior to the web vital.

          var shouldRecord = entry.startTime < firstHidden.firstHiddenTime;

          if (entry.name === 'first-paint' && shouldRecord) {
            _measurements['fp'] = {
              value: entry.startTime,
              unit: 'millisecond'
            };
          }

          if (entry.name === 'first-contentful-paint' && shouldRecord) {
            _measurements['fcp'] = {
              value: entry.startTime,
              unit: 'millisecond'
            };
          }

          break;
        }

      case 'resource':
        {
          _addResourceSpans(span, entry, entry.name, startTime, duration, timeOrigin);

          break;
        }
      // Ignore other entry types.
    }
  });
  _performanceCursor = Math.max(performanceEntries.length - 1, 0);

  _trackNavigator(span); // Measurements are only available for pageload transactions


  if (op === 'pageload') {
    _addTtfbRequestTimeToMeasurements(_measurements);

    var fidMark = _measurements['mark.fid'];

    if (fidMark && _measurements['fid']) {
      // create span for FID
      startAndEndSpan(span, fidMark.value, fidMark.value + msToSec(_measurements['fid'].value), {
        name: 'first input delay',
        op: 'ui.action',
        attributes: {
          [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.ui.browser.metrics'
        }
      }); // Delete mark.fid as we don't want it to be part of final payload

      delete _measurements['mark.fid'];
    } // If FCP is not recorded we should not record the cls value
    // according to the new definition of CLS.
    // TODO: Check if the first condition is still necessary: `onCLS` already only fires once `onFCP` was called.


    if (!('fcp' in _measurements) || !options.recordClsOnPageloadSpan) {
      delete _measurements.cls;
    }

    Object.entries(_measurements).forEach(_ref8 => {
      var [measurementName, measurement] = _ref8;
      setMeasurement(measurementName, measurement.value, measurement.unit);
    }); // Set timeOrigin which denotes the timestamp which to base the LCP/FCP/FP/TTFB measurements on

    span.setAttribute('performance.timeOrigin', timeOrigin); // In prerendering scenarios, where a page might be prefetched and pre-rendered before the user clicks the link,
    // the navigation starts earlier than when the user clicks it. Web Vitals should always be based on the
    // user-perceived time, so they are not reported from the actual start of the navigation, but rather from the
    // time where the user actively started the navigation, for example by clicking a link.
    // This is user action is called "activation" and the time between navigation and activation is stored in
    // the `activationStart` attribute of the "navigation" PerformanceEntry.

    span.setAttribute('performance.activationStart', getActivationStart());

    _setWebVitalAttributes(span);
  }

  _lcpEntry = undefined;
  _clsEntry = undefined;
  _measurements = {};
}
/**
 * Create measure related spans.
 * Exported only for tests.
 */


function _addMeasureSpans(span, entry, startTime, duration, timeOrigin) {
  var navEntry = getNavigationEntry(false);
  var requestTime = msToSec(navEntry ? navEntry.requestStart : 0); // Because performance.measure accepts arbitrary timestamps it can produce
  // spans that happen before the browser even makes a request for the page.
  //
  // An example of this is the automatically generated Next.js-before-hydration
  // spans created by the Next.js framework.
  //
  // To prevent this we will pin the start timestamp to the request start time
  // This does make duration inaccurate, so if this does happen, we will add
  // an attribute to the span

  var measureStartTimestamp = timeOrigin + Math.max(startTime, requestTime);
  var startTimeStamp = timeOrigin + startTime;
  var measureEndTimestamp = startTimeStamp + duration;
  var attributes = {
    [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.resource.browser.metrics'
  };

  if (measureStartTimestamp !== startTimeStamp) {
    attributes['sentry.browser.measure_happened_before_request'] = true;
    attributes['sentry.browser.measure_start_time'] = measureStartTimestamp;
  }

  startAndEndSpan(span, measureStartTimestamp, measureEndTimestamp, {
    name: entry.name,
    op: entry.entryType,
    attributes
  });
  return measureStartTimestamp;
}
/** Instrument navigation entries */


function _addNavigationSpans(span, entry, timeOrigin) {
  ['unloadEvent', 'redirect', 'domContentLoadedEvent', 'loadEvent', 'connect'].forEach(event => {
    _addPerformanceNavigationTiming(span, entry, event, timeOrigin);
  });

  _addPerformanceNavigationTiming(span, entry, 'secureConnection', timeOrigin, 'TLS/SSL');

  _addPerformanceNavigationTiming(span, entry, 'fetch', timeOrigin, 'cache');

  _addPerformanceNavigationTiming(span, entry, 'domainLookup', timeOrigin, 'DNS');

  _addRequest(span, entry, timeOrigin);
}
/** Create performance navigation related spans */


function _addPerformanceNavigationTiming(span, entry, event, timeOrigin) {
  var name = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : event;

  var eventEnd = _getEndPropertyNameForNavigationTiming(event);

  var end = entry[eventEnd];
  var start = entry["".concat(event, "Start")];

  if (!start || !end) {
    return;
  }

  startAndEndSpan(span, timeOrigin + msToSec(start), timeOrigin + msToSec(end), {
    op: "browser.".concat(name),
    name: entry.name,
    attributes: {
      [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.ui.browser.metrics'
    }
  });
}

function _getEndPropertyNameForNavigationTiming(event) {
  if (event === 'secureConnection') {
    return 'connectEnd';
  }

  if (event === 'fetch') {
    return 'domainLookupStart';
  }

  return "".concat(event, "End");
}
/** Create request and response related spans */


function _addRequest(span, entry, timeOrigin) {
  var requestStartTimestamp = timeOrigin + msToSec(entry.requestStart);
  var responseEndTimestamp = timeOrigin + msToSec(entry.responseEnd);
  var responseStartTimestamp = timeOrigin + msToSec(entry.responseStart);

  if (entry.responseEnd) {
    // It is possible that we are collecting these metrics when the page hasn't finished loading yet, for example when the HTML slowly streams in.
    // In this case, ie. when the document request hasn't finished yet, `entry.responseEnd` will be 0.
    // In order not to produce faulty spans, where the end timestamp is before the start timestamp, we will only collect
    // these spans when the responseEnd value is available. The backend (Relay) would drop the entire span if it contained faulty spans.
    startAndEndSpan(span, requestStartTimestamp, responseEndTimestamp, {
      op: 'browser.request',
      name: entry.name,
      attributes: {
        [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.ui.browser.metrics'
      }
    });
    startAndEndSpan(span, responseStartTimestamp, responseEndTimestamp, {
      op: 'browser.response',
      name: entry.name,
      attributes: {
        [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.ui.browser.metrics'
      }
    });
  }
}
/**
 * Create resource-related spans.
 * Exported only for tests.
 */


function _addResourceSpans(span, entry, resourceUrl, startTime, duration, timeOrigin) {
  // we already instrument based on fetch and xhr, so we don't need to
  // duplicate spans here.
  if (entry.initiatorType === 'xmlhttprequest' || entry.initiatorType === 'fetch') {
    return;
  }

  var parsedUrl = parseUrl(resourceUrl);
  var attributes = {
    [SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN]: 'auto.resource.browser.metrics'
  };
  setResourceEntrySizeData(attributes, entry, 'transferSize', 'http.response_transfer_size');
  setResourceEntrySizeData(attributes, entry, 'encodedBodySize', 'http.response_content_length');
  setResourceEntrySizeData(attributes, entry, 'decodedBodySize', 'http.decoded_response_content_length'); // `deliveryType` is experimental and does not exist everywhere

  var deliveryType = entry.deliveryType;

  if (deliveryType != null) {
    attributes['http.response_delivery_type'] = deliveryType;
  } // Types do not reflect this property yet


  var renderBlockingStatus = entry.renderBlockingStatus;

  if (renderBlockingStatus) {
    attributes['resource.render_blocking_status'] = renderBlockingStatus;
  }

  if (parsedUrl.protocol) {
    attributes['url.scheme'] = parsedUrl.protocol.split(':').pop(); // the protocol returned by parseUrl includes a :, but OTEL spec does not, so we remove it.
  }

  if (parsedUrl.host) {
    attributes['server.address'] = parsedUrl.host;
  }

  attributes['url.same_origin'] = resourceUrl.includes(WINDOW.location.origin);
  var {
    name,
    version
  } = extractNetworkProtocol(entry.nextHopProtocol);
  attributes['network.protocol.name'] = name;
  attributes['network.protocol.version'] = version;
  var startTimestamp = timeOrigin + startTime;
  var endTimestamp = startTimestamp + duration;
  startAndEndSpan(span, startTimestamp, endTimestamp, {
    name: resourceUrl.replace(WINDOW.location.origin, ''),
    op: entry.initiatorType ? "resource.".concat(entry.initiatorType) : 'resource.other',
    attributes
  });
}
/**
 * Capture the information of the user agent.
 */


function _trackNavigator(span) {
  var navigator = WINDOW.navigator;

  if (!navigator) {
    return;
  } // track network connectivity


  var connection = navigator.connection;

  if (connection) {
    if (connection.effectiveType) {
      span.setAttribute('effectiveConnectionType', connection.effectiveType);
    }

    if (connection.type) {
      span.setAttribute('connectionType', connection.type);
    }

    if (isMeasurementValue(connection.rtt)) {
      _measurements['connection.rtt'] = {
        value: connection.rtt,
        unit: 'millisecond'
      };
    }
  }

  if (isMeasurementValue(navigator.deviceMemory)) {
    span.setAttribute('deviceMemory', "".concat(navigator.deviceMemory, " GB"));
  }

  if (isMeasurementValue(navigator.hardwareConcurrency)) {
    span.setAttribute('hardwareConcurrency', String(navigator.hardwareConcurrency));
  }
}
/** Add LCP / CLS data to span to allow debugging */


function _setWebVitalAttributes(span) {
  var _clsEntry2;

  if (_lcpEntry) {
    // Capture Properties of the LCP element that contributes to the LCP.
    if (_lcpEntry.element) {
      span.setAttribute('lcp.element', htmlTreeAsString(_lcpEntry.element));
    }

    if (_lcpEntry.id) {
      span.setAttribute('lcp.id', _lcpEntry.id);
    }

    if (_lcpEntry.url) {
      // Trim URL to the first 200 characters.
      span.setAttribute('lcp.url', _lcpEntry.url.trim().slice(0, 200));
    }

    if (_lcpEntry.loadTime != null) {
      // loadTime is the time of LCP that's related to receiving the LCP element response..
      span.setAttribute('lcp.loadTime', _lcpEntry.loadTime);
    }

    if (_lcpEntry.renderTime != null) {
      // renderTime is loadTime + rendering time
      // it's 0 if the LCP element is loaded from a 3rd party origin that doesn't send the
      // `Timing-Allow-Origin` header.
      span.setAttribute('lcp.renderTime', _lcpEntry.renderTime);
    }

    span.setAttribute('lcp.size', _lcpEntry.size);
  } // See: https://developer.mozilla.org/en-US/docs/Web/API/LayoutShift


  if ((_clsEntry2 = _clsEntry) !== null && _clsEntry2 !== void 0 && _clsEntry2.sources) {
    _clsEntry.sources.forEach((source, index) => span.setAttribute("cls.source.".concat(index + 1), htmlTreeAsString(source.node)));
  }
}

function setResourceEntrySizeData(attributes, entry, key, dataKey) {
  var entryVal = entry[key];

  if (entryVal != null && entryVal < MAX_INT_AS_BYTES) {
    attributes[dataKey] = entryVal;
  }
}
/**
 * Add ttfb request time information to measurements.
 *
 * ttfb information is added via vendored web vitals library.
 */


function _addTtfbRequestTimeToMeasurements(_measurements) {
  var navEntry = getNavigationEntry(false);

  if (!navEntry) {
    return;
  }

  var {
    responseStart,
    requestStart
  } = navEntry;

  if (requestStart <= responseStart) {
    _measurements['ttfb.requestTime'] = {
      value: responseStart - requestStart,
      unit: 'millisecond'
    };
  }
}

export { _addMeasureSpans, _addResourceSpans, addPerformanceEntries, startTrackingInteractions, startTrackingLongAnimationFrames, startTrackingLongTasks, startTrackingWebVitals };